/**
 * Text on a path — the shared layout engine.
 *
 * A text element with `textPath` set stops being a wrapped block and becomes one
 * run of glyphs riding a curve inscribed in its box, Figma-style. The box is the
 * path's frame: resize it and the curve (and the text on it) follows.
 *
 * ONE layout function serves both render surfaces:
 *  - the editor draws each glyph as an SVG <text> at a translate+rotate
 *    (`elements/TextOnPath.jsx`),
 *  - the export draws each glyph with the same translate+rotate on Canvas2D
 *    (`drawTextPathElement` in export/renderDocumentToCanvas.js).
 * Advance widths come from ONE Canvas2D measurer here, so both surfaces place
 * every glyph on the same point — a browser's SVG <textPath> would have laid the
 * run out with its own shaping and the export could never have matched it.
 *
 * Consequence worth knowing: glyphs are positioned individually, so kerning pairs
 * and ligatures don't apply along a path (they don't in Figma either).
 */
import { ELEMENT_TYPES } from '../schema/documentSchema';
import { getShapeDef, shapeCommands } from '../schema/shapeLibrary';
import { resolveFontStack } from '../panels/propertyControls';

const TAU = Math.PI * 2;

/** How finely the parametric curves are sampled — 1.5° of tangent resolution. */
const CURVE_SAMPLES = 240;

/** Sample an ellipse inscribed in the box, from angle a0 to a1 (radians). */
function ellipseArc(w, h, a0, a1, steps = CURVE_SAMPLES) {
    const rx = w / 2;
    const ry = h / 2;
    const out = [];
    for (let i = 0; i <= steps; i += 1) {
        const a = a0 + ((a1 - a0) * i) / steps;
        out.push([rx + Math.cos(a) * rx, ry + Math.sin(a) * ry]);
    }
    return out;
}

/** Flatten shape-catalogue commands (M/L/Q/Z) into a polyline. */
function flattenShape(shape, w, h, perCurve = 12) {
    const cmds = shapeCommands({ type: 'shape', shape, ...(getShapeDef(shape).params || {}), styles: {} }, w, h);
    const pts = [];
    let cur = [0, 0];
    cmds.forEach((cmd) => {
        if (cmd.c === 'M' || cmd.c === 'L') {
            cur = [cmd.x, cmd.y];
            pts.push(cur);
        } else if (cmd.c === 'Q') {
            const [x0, y0] = cur;
            for (let i = 1; i <= perCurve; i += 1) {
                const t = i / perCurve;
                const mt = 1 - t;
                pts.push([
                    mt * mt * x0 + 2 * mt * t * cmd.cx + t * t * cmd.x,
                    mt * mt * y0 + 2 * mt * t * cmd.cy + t * t * cmd.y,
                ]);
            }
            cur = [cmd.x, cmd.y];
        }
    });
    return pts;
}

/**
 * The path catalogue. `closed` paths wrap (text can run round for ever); open
 * ones extrapolate along their end tangents, so an over-long run keeps reading
 * straight on instead of piling up at the last point.
 *
 * `start` is where a freshly bound run is centred, as a percentage along the
 * path — the reading position each curve is actually for: the top of a circle,
 * the crown of an arch, the middle of a wave or a straight line, the centre of a
 * rectangle's top edge. Without it every path would start text at its first
 * point, which on an arch is the left end pointing straight up.
 */
export const TEXT_PATH_DEFS = [
    {
        key: 'circle', label: 'Circle', closed: true, start: 0,
        // Starts at the TOP of the box and runs clockwise, like Figma's circle.
        samples: (w, h) => ellipseArc(w, h, -Math.PI / 2, -Math.PI / 2 + TAU),
    },
    {
        key: 'arch', label: 'Arch (up)', closed: false, start: 50,
        samples: (w, h) => ellipseArc(w, h, Math.PI, TAU, CURVE_SAMPLES / 2),
    },
    {
        key: 'valley', label: 'Arch (down)', closed: false, start: 50,
        samples: (w, h) => ellipseArc(w, h, Math.PI, 0, CURVE_SAMPLES / 2),
    },
    {
        key: 'wave', label: 'Wave', closed: false, start: 50,
        samples: (w, h) => {
            const out = [];
            for (let i = 0; i <= CURVE_SAMPLES; i += 1) {
                const t = i / CURVE_SAMPLES;
                out.push([t * w, h / 2 - Math.sin(t * TAU) * (h / 2)]);
            }
            return out;
        },
    },
    {
        key: 'line', label: 'Straight', closed: false, start: 50,
        samples: (w, h) => [[0, h / 2], [w, h / 2]],
    },
    {
        // Perimeter from the top-left corner clockwise, so half the top edge is
        // an eighth of the way round a square.
        key: 'box', label: 'Rectangle', closed: true, start: 12.5,
        samples: (w, h) => [[0, 0], [w, 0], [w, h], [0, h], [0, 0]],
    },
    { key: 'triangle', label: 'Triangle', closed: true, start: 0, samples: (w, h) => flattenShape('triangle', w, h) },
    { key: 'diamond', label: 'Diamond', closed: true, start: 0, samples: (w, h) => flattenShape('diamond', w, h) },
    { key: 'pentagon', label: 'Pentagon', closed: true, start: 0, samples: (w, h) => flattenShape('pentagon', w, h) },
    { key: 'hexagon', label: 'Hexagon', closed: true, start: 0, samples: (w, h) => flattenShape('hexagon', w, h) },
    { key: 'star', label: 'Star', closed: true, start: 0, samples: (w, h) => flattenShape('star', w, h) },
];

const BY_KEY = new Map(TEXT_PATH_DEFS.map((d) => [d.key, d]));

export const DEFAULT_TEXT_PATH = 'circle';

/** Panel options — "None" turns the element back into an ordinary text block. */
export const TEXT_PATH_OPTIONS = [
    { value: 'none', label: 'None' },
    ...TEXT_PATH_DEFS.map((d) => ({ value: d.key, label: d.label })),
];

export const TEXT_PATH_ALIGN_OPTIONS = [
    { value: 'start', label: 'Start' },
    { value: 'center', label: 'Center' },
    { value: 'end', label: 'End' },
];

export function getTextPathDef(key) {
    return BY_KEY.get(key) || null;
}

/**
 * Element fields for the "Text on path" insert. A square box so the default
 * circle really is a circle, and text centred on the top of it.
 */
export const TEXT_PATH_INSERT = {
    textPath: DEFAULT_TEXT_PATH,
    pathStart: 0,
    pathAlign: 'center',
    pathSide: 'outside',
    width: 320,
    height: 320,
    name: 'Text on path',
    content: 'Text on path',
};

/** True when this element renders as a run of glyphs on a curve. */
export function isTextOnPath(el) {
    if (!el) return false;
    const textLike = el.type === ELEMENT_TYPES.TEXT || el.type === ELEMENT_TYPES.PLACEHOLDER;
    return textLike && !!getTextPathDef(el.textPath);
}

export function textPathSamples(el, w = el?.width, h = el?.height) {
    const def = getTextPathDef(el?.textPath);
    if (!def || !(w > 0) || !(h > 0)) return [];
    return def.samples(w, h);
}

const round2 = (v) => Math.round(v * 100) / 100;

/** SVG path data for the curve itself — the editor's on-canvas guide. */
export function textPathD(el, w = el?.width, h = el?.height, step = 2) {
    const pts = textPathSamples(el, w, h);
    if (pts.length < 2) return '';
    const kept = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);
    return kept
        .map(([x, y], i) => `${i ? 'L' : 'M'}${round2(x)} ${round2(y)}`)
        .join('') + (getTextPathDef(el.textPath)?.closed ? 'Z' : '');
}

/* ------------------------------------------------------------------ *
 *  Arc-length walking
 * ------------------------------------------------------------------ */

function arcLengths(pts) {
    const cum = [0];
    for (let i = 1; i < pts.length; i += 1) {
        cum.push(cum[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    }
    return { cum, total: cum[cum.length - 1] || 0 };
}

/** Point + tangent angle (degrees) at `dist` along the polyline. */
function pointAt(pts, cum, total, closed, dist) {
    if (!(total > 0)) return { x: pts[0][0], y: pts[0][1], angle: 0 };
    let d = dist;
    if (closed) d = ((d % total) + total) % total;

    // Outside an open path: keep going along the end tangent rather than stacking
    // every remaining glyph on the last point.
    if (d < 0) return extrapolate(pts, 0, 1, d);
    if (d > total) return extrapolate(pts, pts.length - 2, pts.length - 1, d - total);

    let lo = 0;
    let hi = cum.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (cum[mid] <= d) lo = mid;
        else hi = mid;
    }
    const seg = Math.max(1e-6, cum[hi] - cum[lo]);
    const t = (d - cum[lo]) / seg;
    const [x0, y0] = pts[lo];
    const [x1, y1] = pts[hi];
    return {
        x: x0 + (x1 - x0) * t,
        y: y0 + (y1 - y0) * t,
        angle: (Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI,
    };
}

function extrapolate(pts, i0, i1, over) {
    const [x0, y0] = pts[i0];
    const [x1, y1] = pts[i1];
    const len = Math.hypot(x1 - x0, y1 - y0) || 1;
    const ux = (x1 - x0) / len;
    const uy = (y1 - y0) / len;
    const base = over < 0 ? [x0, y0] : [x1, y1];
    return {
        x: base[0] + ux * over,
        y: base[1] + uy * over,
        angle: (Math.atan2(uy, ux) * 180) / Math.PI,
    };
}

/* ------------------------------------------------------------------ *
 *  Glyph advances — one measurer for both surfaces
 * ------------------------------------------------------------------ */

let measureCtx = null;
const advanceCache = new Map();

export function fontStringOf(styles = {}) {
    const size = Number(styles.fontSize) || 16;
    return `${styles.fontStyle || 'normal'} ${styles.fontWeight || 400} ${size}px ${resolveFontStack(styles)}`;
}

/** Advance width of one glyph, cached per font+glyph. */
export function measureGlyph(font, ch) {
    const key = `${font} ${ch}`;
    const hit = advanceCache.get(key);
    if (hit != null) return hit;
    if (!measureCtx) {
        if (typeof document === 'undefined') return 0;
        measureCtx = document.createElement('canvas').getContext('2d');
    }
    measureCtx.font = font;
    const w = measureCtx.measureText(ch).width;
    advanceCache.set(key, w);
    return w;
}

/**
 * Fonts load after the first paint, so an advance measured against a fallback
 * face must not stick. The font pipeline calls this once the real faces are in.
 */
export function clearGlyphMetrics() {
    advanceCache.clear();
}

function applyTransform(text, transform) {
    const raw = String(text ?? '');
    if (transform === 'uppercase') return raw.toUpperCase();
    if (transform === 'lowercase') return raw.toLowerCase();
    if (transform === 'capitalize') return raw.replace(/\b\w/g, (c) => c.toUpperCase());
    return raw;
}

/** The string that rides the path — one line, so newlines become spaces. */
export function textPathContent(el) {
    const raw = el?.content
        || (el?.type === ELEMENT_TYPES.PLACEHOLDER ? `{{${el.placeholderKey || 'field'}}}` : '');
    return applyTransform(raw, el?.styles?.textTransform).replace(/\s*\n\s*/g, ' ');
}

/**
 * The same run as code points, each remembering where it came from in `content`.
 *
 * Per-character colours are stored as offsets into the element's own text, and a
 * path run is not that text: line breaks (and the whitespace around them) collapse
 * into single spaces. Carrying the source index per glyph is what lets a coloured
 * word stay coloured once its text is bound to a curve.
 */
export function textPathGlyphSources(el) {
    const raw = el?.content
        || (el?.type === ELEMENT_TYPES.PLACEHOLDER ? `{{${el.placeholderKey || 'field'}}}` : '');
    // Every transform on offer maps one character to one character, so an index
    // into the transformed string is an index into the original.
    const text = applyTransform(raw, el?.styles?.textTransform);
    const out = [];
    const pushRange = (from, to) => {
        let i = from;
        while (i < to) {
            const ch = String.fromCodePoint(text.codePointAt(i));
            out.push({ ch, index: i });
            i += ch.length;
        }
    };
    const breaks = /\s*\n\s*/g;
    let last = 0;
    let m = breaks.exec(text);
    while (m) {
        pushRange(last, m.index);
        out.push({ ch: ' ', index: m.index });
        last = m.index + m[0].length;
        m = breaks.exec(text);
    }
    pushRange(last, text.length);
    return out;
}

/**
 * Lay the element's text out along its path.
 *
 * Each glyph gets the point and tangent at the middle of its own advance, and is
 * drawn with its baseline on the path and its ink centred on that point, i.e.
 * translate(x, y) rotate(angle) then draw at x = -advance / 2.
 *
 * @returns {{glyphs: Array, length: number}|null}
 */
export function layoutTextOnPath(el, w = el?.width, h = el?.height) {
    const pts = textPathSamples(el, w, h);
    if (pts.length < 2) return null;
    const def = getTextPathDef(el.textPath);
    const { cum, total } = arcLengths(pts);

    const styles = el.styles || {};
    const font = fontStringOf(styles);
    const spacing = Number(styles.letterSpacing) || 0;
    const sources = textPathGlyphSources(el);
    const chars = sources.map((s) => s.ch);
    if (!chars.length) return { glyphs: [], length: total };

    const inks = chars.map((ch) => measureGlyph(font, ch));
    // Letter spacing sits BETWEEN glyphs, as in CSS: the run is the sum of the
    // advances plus one gap per join.
    const runLength = inks.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);

    let start = ((Number(el.pathStart) || 0) / 100) * total;
    if (el.pathAlign === 'center') start -= runLength / 2;
    else if (el.pathAlign === 'end') start -= runLength;

    // "Inside" walks the path backwards and turns every glyph over, so the run
    // still reads left to right — the flip Figma offers on a closed path.
    const flip = el.pathSide === 'inside';

    const glyphs = [];
    let run = 0;
    chars.forEach((ch, i) => {
        const ink = inks[i];
        const centre = run + ink / 2;
        const at = pointAt(pts, cum, total, !!def.closed, flip ? start - centre : start + centre);
        glyphs.push({
            ch,
            x: at.x,
            y: at.y,
            angle: at.angle + (flip ? 180 : 0),
            advance: ink,
            // Where this glyph sits in the element's own text — for per-character colour.
            index: sources[i].index,
        });
        run += ink + spacing;
    });
    return { glyphs, length: total };
}
